// How a client names a project it can open (PROTOCOL.md §10): its own projects by their id, as always, and a project
// shared with it as `<owner>/<projectId>`. Ids are `[A-Za-z0-9_-]{1,64}` (§ conventions), so an address with a `/` can
// never be one of the account's own ids: an owner's project and a member's own project with the same id (the sample's,
// say, which every account has) are two addresses, and every store keyed by address keeps them apart — the offline
// cache, the outbox, archives, the Library's figures and each per-browser preference.
const ID = /^[A-Za-z0-9_-]{1,64}$/;

export interface ProjectAddress {
  /** The owner's account id, for a project shared with this account; null for the account's own. */
  owner: string | null;
  projectId: string;
}

/** The address string: `projectId` for one's own, `owner/projectId` for a shared project. */
export function addressOf(owner: string | null, projectId: string): string {
  return owner ? `${owner}/${projectId}` : projectId;
}

/** An address string parsed (null when it isn't one). */
export function parseAddress(address: string): ProjectAddress | null {
  const slash = address.indexOf('/');
  if (slash < 0) return ID.test(address) ? { owner: null, projectId: address } : null;
  const owner = address.slice(0, slash), projectId = address.slice(slash + 1);
  return ID.test(owner) && ID.test(projectId) ? { owner, projectId } : null;
}

/** Whether an address names a project shared with this account (not its own). */
export const isShared = (address: string) => address.includes('/');

/** The project id inside an address (the owner's id for a shared project). */
export const projectIdOf = (address: string) => parseAddress(address)?.projectId ?? address;

/** The owner named in an address, or null for the account's own. */
export const ownerOf = (address: string) => parseAddress(address)?.owner ?? null;

/** The path segment(s) naming the project in routes and the sync path: `{id}` or `{owner}/{id}`, each part encoded. */
export function addressPath(address: string): string {
  const a = parseAddress(address);
  if (!a) return encodeURIComponent(address);
  return a.owner ? `${encodeURIComponent(a.owner)}/${encodeURIComponent(a.projectId)}` : encodeURIComponent(a.projectId);
}
